import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { apiPut } from '@/api/axios-client'
import { getErrorMessage } from '@/api/errors'
import { qk } from '@/api/query-keys'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { newRowId, type Metric, type Risk } from '@/features/projects'
import { TEAM_LONG, TEAMS, type Dashboard } from './model'

/**
 * Editing the plan's risks and success metrics in place on the client dashboard, rather than on
 * the project page's plan. Both write the same plan the project page edits — one PUT carrying
 * only the list that changed, with the plan's version so two editors cannot overwrite each other.
 */

const LEVELS = ['low', 'medium', 'high'] as const
const STATUSES = ['open', 'mitigated', 'closed', 'occurred'] as const

function useSavePlanPart(projectId: string, done: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPut(`project-plans/${encodeURIComponent(projectId)}`, body),
    retry: false,
    onSuccess: () => {
      toast.success(done)
      void qc.invalidateQueries({ queryKey: qk.lifecycle(projectId) })
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  })
}

const pick = <T extends string>(label: string, value: T, options: readonly T[], onChange: (v: T) => void, names?: Record<string, string>) => (
  <Select value={value} onValueChange={(v) => onChange(v as T)}>
    <SelectTrigger className="h-8 text-xs" aria-label={label}><SelectValue /></SelectTrigger>
    <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{names?.[o] ?? `${label}: ${o}`}</SelectItem>)}</SelectContent>
  </Select>
)

export function RiskEditor({ projectId, plan, onDone }: { projectId: string; plan: Dashboard['plan']; onDone: () => void }) {
  const [rows, setRows] = useState<Risk[]>(() => plan.risks.map((r) => ({ ...r })))
  const save = useSavePlanPart(projectId, 'Risk register saved')
  const set = (i: number, patch: Partial<Risk>) => setRows((x) => x.map((r, j) => (j === i ? { ...r, ...patch } : r)))
  return (
    <div className="grid gap-2">
      {rows.map((r, i) => (
        <div key={r.id} className="grid gap-2 rounded-lg border border-border p-2.5">
          <div className="flex gap-2">
            <Input aria-label="Risk" placeholder="What could go wrong" className="h-8" value={r.text} onChange={(e) => set(i, { text: e.target.value })} />
            <button type="button" aria-label="Remove risk" className="grid size-8 shrink-0 place-items-center rounded-md text-muted-foreground hover:text-danger" onClick={() => setRows((x) => x.filter((_, j) => j !== i))}><Trash2 className="size-4" /></button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {pick('Likelihood', (r.likelihood ?? r.severity) as (typeof LEVELS)[number], LEVELS, (v) => set(i, { likelihood: v, severity: v }))}
            {pick('Impact', (r.impact ?? 'medium') as (typeof LEVELS)[number], LEVELS, (v) => set(i, { impact: v }))}
            {pick('Owner', (r.ownerTeam ?? 'am') as (typeof TEAMS)[number], TEAMS, (v) => set(i, { ownerTeam: v }), TEAM_LONG)}
            {pick('Status', r.status, STATUSES, (v) => set(i, { status: v }))}
          </div>
          <Input aria-label="Mitigation" placeholder="Mitigation" className="h-8" value={r.mitigation} onChange={(e) => set(i, { mitigation: e.target.value })} />
        </div>
      ))}
      <Button type="button" variant="ghost" size="sm" className="justify-self-start text-brand" onClick={() => setRows((x) => [...x, { id: newRowId(), text: '', severity: 'medium', likelihood: 'medium', impact: 'medium', ownerTeam: 'am', mitigation: '', status: 'open' }])}>
        <Plus className="size-3.5" /> Add a risk
      </Button>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={onDone} disabled={save.isPending}>Cancel</Button>
        <Button
          size="sm"
          disabled={save.isPending}
          onClick={() =>
            save.mutate(
              { risks: rows.filter((r) => r.text.trim()).map((r) => ({ ...r, text: r.text.trim(), mitigation: r.mitigation.trim() })), ...(plan.version !== null ? { version: plan.version } : {}) },
              { onSuccess: onDone },
            )
          }
        >
          {save.isPending ? 'Saving…' : 'Save risks'}
        </Button>
      </div>
    </div>
  )
}

interface MetricDraft { id: string; name: string; unit: string; baseline: string; target: string; current: string; due: string }
const num = (s: string) => (s.trim() === '' || !Number.isFinite(Number(s)) ? null : Number(s))
const txt = (n: number | null) => (n === null ? '' : String(n))
const dayOf = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString('en-CA') : '')

export function MetricsListEditor({ projectId, plan, onDone }: { projectId: string; plan: Dashboard['plan']; onDone: () => void }) {
  const [rows, setRows] = useState<MetricDraft[]>(() =>
    plan.metrics.map((m) => ({ id: m.id, name: m.name, unit: m.unit, baseline: txt(m.baseline), target: txt(m.target), current: txt(m.current), due: dayOf(m.dueAt) })),
  )
  const [error, setError] = useState<string | null>(null)
  const save = useSavePlanPart(projectId, 'Success metrics saved')
  const set = (i: number, patch: Partial<MetricDraft>) => setRows((x) => x.map((r, j) => (j === i ? { ...r, ...patch } : r)))
  const submit = () => {
    const kept = rows.filter((r) => r.name.trim())
    const bad = kept.find((r) => [r.baseline, r.target, r.current].some((v) => v.trim() !== '' && num(v) === null))
    if (bad) return setError(`“${bad.name}” has a value that is not a number.`)
    setError(null)
    const metrics: Metric[] = kept.map((r) => ({
      id: r.id,
      name: r.name.trim(),
      unit: r.unit.trim(),
      baseline: num(r.baseline),
      target: num(r.target),
      current: num(r.current),
      dueAt: r.due ? new Date(`${r.due}T17:00:00`).toISOString() : null,
    }))
    save.mutate({ metrics, ...(plan.version !== null ? { version: plan.version } : {}) }, { onSuccess: onDone })
  }
  return (
    <div className="grid gap-2">
      <div className="hidden grid-cols-[2fr_1fr_1fr_1fr_1fr_1.3fr_auto] gap-2 px-1 text-tiny font-semibold uppercase tracking-wide text-text-muted md:grid">
        <span>Metric</span><span>Unit</span><span>Baseline</span><span>Target</span><span>Latest</span><span>Due</span><span className="w-8" />
      </div>
      {rows.map((r, i) => (
        <div key={r.id} className="grid grid-cols-2 gap-2 rounded-lg border border-border p-2 md:grid-cols-[2fr_1fr_1fr_1fr_1fr_1.3fr_auto] md:border-0 md:p-0">
          <Input aria-label="Metric" placeholder="e.g. Time to hire" className="col-span-2 h-8 md:col-span-1" value={r.name} onChange={(e) => set(i, { name: e.target.value })} />
          <Input aria-label="Unit" placeholder="days" className="h-8" value={r.unit} onChange={(e) => set(i, { unit: e.target.value })} />
          <Input aria-label="Baseline" inputMode="decimal" placeholder="Baseline" className="h-8" value={r.baseline} onChange={(e) => set(i, { baseline: e.target.value })} />
          <Input aria-label="Target" inputMode="decimal" placeholder="Target" className="h-8" value={r.target} onChange={(e) => set(i, { target: e.target.value })} />
          <Input aria-label="Latest" inputMode="decimal" placeholder="Latest" className="h-8" value={r.current} onChange={(e) => set(i, { current: e.target.value })} />
          <Input aria-label="Due" type="date" className="h-8" value={r.due} onChange={(e) => set(i, { due: e.target.value })} />
          <button type="button" aria-label="Remove metric" className="grid size-8 place-items-center rounded-md text-muted-foreground hover:text-danger" onClick={() => setRows((x) => x.filter((_, j) => j !== i))}><Trash2 className="size-4" /></button>
        </div>
      ))}
      <Button type="button" variant="ghost" size="sm" className="justify-self-start text-brand" onClick={() => setRows((x) => [...x, { id: newRowId(), name: '', unit: '', baseline: '', target: '', current: '', due: '' }])}>
        <Plus className="size-3.5" /> Add a metric
      </Button>
      {error ? <p role="alert" className="text-sm text-danger">{error}</p> : null}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={onDone} disabled={save.isPending}>Cancel</Button>
        <Button size="sm" disabled={save.isPending} onClick={submit}>{save.isPending ? 'Saving…' : 'Save metrics'}</Button>
      </div>
    </div>
  )
}
